import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  CardGrid,
  EmptyState,
  Filters,
  Metric,
  Table,
  Toolbar,
  type Column,
} from '@evogard/ui';
import { Chart } from '@evogard/ui/chart';
import { Tabs, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import { count, format, loadFailureMessage, type Money } from '@evogard/domain';
import type {
  Period,
  SalesComparison,
  SalesComparisonMonth,
  SalesComparisonSession,
} from '@evogard/contract';
import { api } from '../../lib/api.js';
import { useSessionContext } from '../../lib/session.js';
import {
  CLASSIFICATION_LABEL,
  ProfileBadge,
  currentPeriod,
  periodLabel,
} from './common.js';
import { VariationBadge, centsOf, compactMoney, variationOf } from './reportFormat.js';

/**
 * A Comparação de meses — a segunda aba do Relatório de Vendas.
 *
 * ## Duas competências quaisquer, no MESMO recorte
 *
 * O mês de base e o de referência, e o recorte de pessoa e de dia vale para os
 * dois — é o que torna os números comparáveis. Consultor, time e dias moram no
 * endereço junto com o Painel: trocar de aba não perde o recorte.
 *
 * ## O que a tela calcula, e o que ela só mostra
 *
 * Os totais e a série de cada dia vêm do servidor (`shapeSalesComparison`). A
 * diferença, a variação e o ACUMULADO do gráfico são aritmética de tela sobre
 * esses números — nenhum deles decide dinheiro de ninguém.
 *
 * ## O acumulado para no dia que o mês alcançou
 *
 * No mês corrente, os dias depois de hoje não existem ainda: a linha para em
 * hoje em vez de seguir reta até o dia 30, que desenharia um mês "parado" que
 * ninguém mediu. E o mês de 28 dias acaba no 28 — o eixo é o do mais longo.
 *
 * ## As sessões são as categorias da casa
 *
 * Geral (toda placa, inclusive a reativação e a sem classificação), Vendas,
 * Trocas, Reativações e Plano Bronze — `PLATE_CLASSIFICATIONS`, com os rótulos
 * de `CLASSIFICATION_LABEL`. Não há "venda pura" separada da venda: é a mesma
 * categoria.
 */
export function ComparisonView() {
  const { data: context } = useSessionContext();
  const search = useSearch({ from: '/authenticated/sales/reports' });
  const navigate = useNavigate();
  const tenantId = context?.currentTenant.id;

  const reference = (search.reference ?? currentPeriod()) as Period;
  const baseline = (search.baseline ?? previousPeriod(reference)) as Period;
  const session: SalesComparisonSession = search.session ?? 'venda';
  const days = [...new Set(search.days ?? [])].sort((a, b) => a - b);

  const query = useQuery({
    queryKey: [
      'comercial',
      'relatorio',
      'comparacao',
      tenantId,
      baseline,
      reference,
      search.consultant,
      search.team,
      days.join(','),
    ],
    enabled: context !== undefined,
    queryFn: async () => {
      const r = await api.sales.salesComparison({
        query: {
          baseline,
          reference,
          consultantId: search.consultant,
          teamId: search.team,
          days: days.length === 0 ? undefined : days.join(','),
        },
      });
      if (r.status !== 200) {
        throw new Error(loadFailureMessage(r.status, 'a comparação de meses'));
      }
      return r.body;
    },
    /* Só dentro da mesma base — os números de uma praça sob o nome da outra. */
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[3] === tenantId ? previous : undefined,
  });

  const data = query.data;
  const options = data?.options;

  return (
    <>
      <Toolbar
        toolbar={
          <Filters
            values={{
              baseline: search.baseline ?? '',
              reference: search.reference ?? '',
              consultant: search.consultant ? String(search.consultant) : '',
              team: search.team ? String(search.team) : '',
              days: days.map(String),
            }}
            onApply={(values) => {
              const nextBaseline = String(values.baseline ?? '');
              const nextReference = String(values.reference ?? '');
              const consultant = String(values.consultant ?? '');
              const team = String(values.team ?? '');
              const nextDays = arrayOf(values.days).map(Number);
              void navigate({
                to: '/sales/reports',
                search: {
                  ...search,
                  baseline: nextBaseline === '' ? undefined : nextBaseline,
                  reference: nextReference === '' ? undefined : nextReference,
                  consultant: consultant === '' ? undefined : Number(consultant),
                  /* Consultor escolhido vence o time, como no Painel. */
                  team: team === '' || consultant !== '' ? undefined : Number(team),
                  days: nextDays.length === 0 ? undefined : nextDays,
                  /* As semanas são do mês do Painel, e não fazem sentido em dois meses. */
                  weeks: undefined,
                },
                replace: true,
              });
            }}
            fields={[
              {
                type: 'periodo',
                name: 'baseline',
                label: 'Mês de base',
                help: 'Vazio é o mês anterior ao de referência.',
              },
              {
                type: 'periodo',
                name: 'reference',
                label: 'Mês de referência',
                help: 'Vazio é o mês de hoje.',
              },
              {
                type: 'selecao',
                name: 'consultant',
                label: 'Consultor',
                placeholder: 'Todos os consultores',
                help: 'Escolhido o consultor, o time é ignorado.',
                options: (options?.consultants ?? []).map((c) => ({
                  value: String(c.id),
                  label: c.nome,
                })),
              },
              {
                type: 'selecao',
                name: 'team',
                label: 'Time',
                placeholder: 'Todos os times',
                help: 'Pela composição de hoje do time, nos dois meses.',
                options: (options?.teams ?? []).map((t) => ({
                  value: String(t.id),
                  label: t.nome,
                })),
              },
              {
                type: 'multipla',
                name: 'days',
                label: 'Dias considerados',
                placeholder: 'Todos os dias',
                help: 'Os mesmos dias nos dois meses.',
                options: Array.from({ length: 31 }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1).padStart(2, '0'),
                })),
              },
            ]}
          />
        }
      />

      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}

      {query.isPending && !query.isError && (
        <Card title="Carregando…">
          <span className="esqueleto block h-4 w-2/3" />
          <span className="esqueleto mt-2 block h-4 w-1/2" />
        </Card>
      )}

      {data !== undefined && (
        <ComparisonBody
          data={data}
          days={days}
          session={session}
          onSession={(next) =>
            void navigate({
              to: '/sales/reports',
              search: { ...search, session: next === 'venda' ? undefined : next },
              replace: true,
            })
          }
        />
      )}
    </>
  );
}

// @valor
const SESSIONS: readonly {
  value: SalesComparisonSession;
  label: string;
  detail: string;
}[] = [
  { value: 'geral', label: 'Geral', detail: 'Toda placa, de qualquer categoria' },
  { value: 'venda', label: CLASSIFICATION_LABEL.venda, detail: 'Venda nova' },
  { value: 'troca', label: CLASSIFICATION_LABEL.troca, detail: 'Troca de veículo' },
  {
    value: 'reativacao',
    label: CLASSIFICATION_LABEL.reativacao,
    detail: 'Contrato reativado',
  },
  { value: 'bronze', label: CLASSIFICATION_LABEL.bronze, detail: 'Cadastro Bronze' },
];

function ComparisonBody({
  data,
  days,
  session,
  onSession,
}: {
  data: SalesComparison;
  days: readonly number[];
  session: SalesComparisonSession;
  onSession: (session: SalesComparisonSession) => void;
}) {
  const baseline = sessionOf(data.baseline, session);
  const reference = sessionOf(data.reference, session);
  const baselineLabel = periodLabel(data.baseline.period);
  const referenceLabel = periodLabel(data.reference.period);
  const nothing =
    sessionOf(data.baseline, 'geral').plates === 0 &&
    sessionOf(data.reference, 'geral').plates === 0;

  return (
    <>
      <Card
        title="O que está sendo comparado"
        descricao={
          days.length === 0
            ? 'Considerando todos os dias de cada mês.'
            : `Considerando só ${count(days.length, 'dia', 'dias')}: ${days.join(', ')}.`
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-texto">
            {/* Neutros: a cor de cada mês é a da linha dele no gráfico, e um
                selo de outra cor ao lado ensinaria duas legendas. */}
            <Badge tone="neutro">Base · {baselineLabel}</Badge>
            <span className="text-texto-suave">contra</span>
            <Badge tone="neutro">Referência · {referenceLabel}</Badge>
          </div>

          <Tabs
            value={session}
            onValueChange={(v) => onSession(v as SalesComparisonSession)}
          >
            <TabsList aria-label="Sessão analisada" className="flex-wrap">
              {SESSIONS.map((s) => (
                <TabsTrigger key={s.value} value={s.value} title={s.detail}>
                  {s.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <p className="text-xs text-texto-suave">
            {SESSIONS.find((s) => s.value === session)?.detail}.
          </p>
        </div>
      </Card>

      {nothing ? (
        <EmptyState icon="grafico" title="Nenhuma venda nos dois meses deste recorte.">
          Nem em {baselineLabel}, nem em {referenceLabel}. Troque os meses, ou tire o
          filtro de consultor, time ou dia.
        </EmptyState>
      ) : (
        <CardGrid>
          <ComparisonCard
            title="Quantidade"
            descricao="Placas da sessão, acumuladas ao longo dos dias."
            baselineLabel={baselineLabel}
            referenceLabel={referenceLabel}
            baseline={baseline.plates}
            reference={reference.plates}
            format={(n) => n.toLocaleString('pt-BR')}
            series={{
              baseline: cumulative(
                baseline.daily.map((d) => d.plates),
                data.baseline.throughDay,
                days,
              ),
              reference: cumulative(
                reference.daily.map((d) => d.plates),
                data.reference.throughDay,
                days,
              ),
            }}
          />
          <ComparisonCard
            title="Adesão bruta"
            descricao={
              baseline.withoutAmount + reference.withoutAmount > 0
                ? `A soma da adesão declarada, acumulada. ${count(baseline.withoutAmount + reference.withoutAmount, 'venda sem valor fica', 'vendas sem valor ficam')} de fora.`
                : 'A soma da adesão declarada, acumulada ao longo dos dias.'
            }
            baselineLabel={baselineLabel}
            referenceLabel={referenceLabel}
            baseline={centsOf(baseline.adhesion)}
            reference={centsOf(reference.adhesion)}
            format={(cents) => format(cents as Money)}
            axisFormat={compactMoney}
            series={{
              baseline: cumulative(
                baseline.daily.map((d) => centsOf(d.adhesion)),
                data.baseline.throughDay,
                days,
              ),
              reference: cumulative(
                reference.daily.map((d) => centsOf(d.adhesion)),
                data.reference.throughDay,
                days,
              ),
            }}
          />
        </CardGrid>
      )}

      {!nothing && (
        <>
          <VehicleComparison
            baseline={baseline}
            reference={reference}
            baselineLabel={baselineLabel}
            referenceLabel={referenceLabel}
          />
          <ConsultantComparison
            data={data}
            baseline={baseline}
            reference={reference}
            baselineLabel={baselineLabel}
            referenceLabel={referenceLabel}
          />
        </>
      )}
    </>
  );
}

/* --- as tabelas lado a lado ------------------------------------------------ */

type SessionFigures = ReturnType<typeof sessionOf>;

/** Uma linha comparada: o mesmo nome nos dois meses. */
interface ComparedRow {
  key: string;
  name: string;
  detail?: ReactNode;
  baseline: { plates: number; adhesion: number };
  reference: { plates: number; adhesion: number };
}

const EMPTY = { plates: 0, adhesion: 0 };

/**
 * A tabela das duas comparações — por tipo de veículo e por consultor.
 *
 * Diferença e variação são aritmética de tela sobre os dois números da linha.
 * A variação sem base (zero no mês de base) diz "novo", e não um percentual.
 */
function ComparedTable({
  title,
  descricao,
  label,
  empty,
  rows,
  baselineLabel,
  referenceLabel,
  nameTitle,
}: {
  title: string;
  descricao: string;
  label: string;
  empty: string;
  rows: ComparedRow[];
  baselineLabel: string;
  referenceLabel: string;
  nameTitle: string;
}) {
  const columns: Column<ComparedRow>[] = [
    {
      title: nameTitle,
      alignment: 'inicio',
      cell: (r) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-medium text-texto">{r.name}</span>
          {r.detail}
        </span>
      ),
    },
    {
      title: `Placas · ${shortMonth(baselineLabel)}`,
      alignment: 'fim',
      cell: (r) => <span className="tabular-nums">{r.baseline.plates}</span>,
    },
    {
      title: `Placas · ${shortMonth(referenceLabel)}`,
      alignment: 'fim',
      cell: (r) => <span className="tabular-nums">{r.reference.plates}</span>,
    },
    {
      title: 'Diferença',
      alignment: 'fim',
      cell: (r) => <Difference value={r.reference.plates - r.baseline.plates} />,
    },
    {
      title: 'Variação',
      alignment: 'fim',
      cell: (r) => (
        <Variation current={r.reference.plates} previous={r.baseline.plates} />
      ),
    },
    {
      title: `Adesão · ${shortMonth(referenceLabel)}`,
      alignment: 'fim',
      cell: (r) => (
        <span className="tabular-nums">{format(r.reference.adhesion as Money)}</span>
      ),
    },
    {
      title: 'Diferença de adesão',
      alignment: 'fim',
      cell: (r) => (
        <Difference
          value={r.reference.adhesion - r.baseline.adhesion}
          format={(cents) => format(cents as Money)}
        />
      ),
    },
  ];

  return (
    <Card title={title} descricao={descricao} bleed>
      <Table<ComparedRow>
        columns={columns}
        items={rows}
        rowKey={(r) => r.key}
        label={label}
        empty={empty}
      />
    </Card>
  );
}

function VehicleComparison({
  baseline,
  reference,
  baselineLabel,
  referenceLabel,
}: {
  baseline: SessionFigures;
  reference: SessionFigures;
  baselineLabel: string;
  referenceLabel: string;
}) {
  const byLabel = new Map<string, ComparedRow>();
  const row = (label: string | null) => {
    const key = label ?? '—';
    const found = byLabel.get(key) ?? {
      key,
      name: label ?? 'Sem veículo no cadastro',
      baseline: EMPTY,
      reference: EMPTY,
    };
    byLabel.set(key, found);
    return found;
  };
  for (const t of baseline.vehicleTypes) {
    row(t.label).baseline = { plates: t.plates, adhesion: centsOf(t.adhesion) };
  }
  for (const t of reference.vehicleTypes) {
    row(t.label).reference = { plates: t.plates, adhesion: centsOf(t.adhesion) };
  }

  return (
    <ComparedTable
      title="Por tipo de veículo"
      descricao="As placas da sessão pelo tipo que o ERP dá ao veículo, nos dois meses."
      label="Comparação por tipo de veículo"
      empty="Nenhuma placa da sessão nos dois meses."
      nameTitle="Tipo"
      rows={sortedRows([...byLabel.values()])}
      baselineLabel={baselineLabel}
      referenceLabel={referenceLabel}
    />
  );
}

/**
 * O resumo por consultor, lado a lado. Todo consultor ativo do recorte aparece,
 * inclusive quem não vendeu em nenhum dos dois meses — a linha zerada é
 * informação. A venda sem consultor casado vira uma linha própria, no fim.
 */
function ConsultantComparison({
  data,
  baseline,
  reference,
  baselineLabel,
  referenceLabel,
}: {
  data: SalesComparison;
  baseline: SessionFigures;
  reference: SessionFigures;
  baselineLabel: string;
  referenceLabel: string;
}) {
  const figures = (session: SessionFigures, id: number | null) => {
    const found = session.consultants.find((c) => c.consultantId === id);
    return found === undefined
      ? EMPTY
      : { plates: found.plates, adhesion: centsOf(found.adhesion) };
  };

  const people: ComparedRow[] = data.consultants.map((c) => ({
    key: String(c.consultant.id),
    name: c.consultant.user.nome,
    detail: (
      <span className="flex flex-wrap items-center gap-1.5 text-xs text-texto-suave">
        {c.team?.nome ?? 'Sem time'}
        <ProfileBadge profile={c.profile} />
      </span>
    ),
    baseline: figures(baseline, c.consultant.id),
    reference: figures(reference, c.consultant.id),
  }));

  const orphanBaseline = figures(baseline, null);
  const orphanReference = figures(reference, null);
  const orphans: ComparedRow[] =
    orphanBaseline.plates + orphanReference.plates === 0
      ? []
      : [
          {
            key: 'sem-consultor',
            name: 'Sem consultor',
            detail: (
              <span className="text-xs text-texto-suave">
                O voluntário do ERP ainda não tem vínculo em Consultores e times.
              </span>
            ),
            baseline: orphanBaseline,
            reference: orphanReference,
          },
        ];

  return (
    <ComparedTable
      title="Resumo por consultor"
      descricao="Todo consultor ativo do recorte, de qualquer perfil, e quem vendeu num dos dois meses. O time é o de hoje."
      label="Comparação por consultor"
      empty="Nenhum consultor neste recorte."
      nameTitle="Consultor"
      rows={[...sortedRows(people), ...orphans]}
      baselineLabel={baselineLabel}
      referenceLabel={referenceLabel}
    />
  );
}

function sortedRows(rows: ComparedRow[]): ComparedRow[] {
  return rows.sort(
    (a, b) =>
      b.reference.plates - a.reference.plates ||
      b.baseline.plates - a.baseline.plates ||
      a.name.localeCompare(b.name, 'pt-BR'),
  );
}

function Difference({
  value,
  format: formatValue = (n) => n.toLocaleString('pt-BR'),
}: {
  value: number;
  format?: (n: number) => string;
}) {
  return (
    <span
      className={
        value < 0
          ? 'font-medium text-erro tabular-nums'
          : value > 0
            ? 'font-medium text-sucesso tabular-nums'
            : 'text-texto-suave tabular-nums'
      }
    >
      {value > 0 ? '+' : ''}
      {formatValue(value)}
    </span>
  );
}

function Variation({ current, previous }: { current: number; previous: number }) {
  const change = variationOf(current, previous);
  if (change === null) {
    return (
      <span className="text-xs text-texto-suave">{current === 0 ? '—' : 'novo'}</span>
    );
  }
  return <VariationBadge change={change} />;
}

/** "agosto de 2026" → "ago/26", para caber no cabeçalho da coluna. */
function shortMonth(label: string): string {
  const [month, , year] = label.split(' ');
  return `${(month ?? '').slice(0, 3)}/${(year ?? '').slice(2)}`;
}

interface CumulativePoint {
  label: string;
  baseline: number | null;
  reference: number | null;
}

function ComparisonCard({
  title,
  descricao,
  baselineLabel,
  referenceLabel,
  baseline,
  reference,
  format: formatValue,
  axisFormat,
  series,
}: {
  title: string;
  descricao: string;
  baselineLabel: string;
  referenceLabel: string;
  baseline: number;
  reference: number;
  format: (n: number) => string;
  axisFormat?: (n: number) => string;
  series: { baseline: (number | null)[]; reference: (number | null)[] };
}) {
  const change = variationOf(reference, baseline);
  const difference = reference - baseline;
  const length = Math.max(series.baseline.length, series.reference.length);
  const points: CumulativePoint[] = Array.from({ length }, (_, i) => ({
    label: String(i + 1).padStart(2, '0'),
    baseline: series.baseline[i] ?? null,
    reference: series.reference[i] ?? null,
  }));

  return (
    <Card
      title={title}
      descricao={descricao}
      actions={
        change === null ? (
          <Badge tone="neutro">sem base para comparar</Badge>
        ) : (
          <VariationBadge change={change} />
        )
      }
    >
      <div className="flex flex-col gap-4">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Metric label={`Base · ${baselineLabel}`} value={formatValue(baseline)} />
          <Metric
            label={`Referência · ${referenceLabel}`}
            value={formatValue(reference)}
          />
          <Metric
            label="Diferença"
            value={`${difference > 0 ? '+' : ''}${formatValue(difference)}`}
            tone={difference < 0 ? 'erro' : undefined}
          />
        </dl>

        <Chart<CumulativePoint>
          kind="linha"
          title="Evolução acumulada por dia"
          label={`${title}: evolução acumulada de ${baselineLabel} e ${referenceLabel}`}
          data={points}
          empty="Nenhuma venda com dia conhecido nos dois meses."
          category={(p) => p.label}
          series={[
            {
              key: 'baseline',
              label: capitalized(baselineLabel),
              value: (p) => p.baseline,
            },
            {
              key: 'reference',
              label: capitalized(referenceLabel),
              value: (p) => p.reference,
            },
          ]}
          format={formatValue}
          axisFormat={axisFormat}
          height={240}
        />
      </div>
    </Card>
  );
}

/* --- aritmética de tela --------------------------------------------------- */

function sessionOf(month: SalesComparisonMonth, session: SalesComparisonSession) {
  const found = month.sessions.find((s) => s.session === session);
  if (found === undefined) {
    throw new Error(`A comparação não trouxe a sessão ${session} de ${month.period}.`);
  }
  return found;
}

/**
 * O acumulado dia a dia, parando no dia que o mês alcançou: depois dele o
 * ponto é `null` — não medido —, e não o último valor repetido. Com dias
 * escolhidos, para também depois do último deles: seguir reta até o dia 31
 * desenharia um mês parado que ninguém pediu para ver.
 */
export function cumulative(
  values: readonly number[],
  throughDay: number,
  days: readonly number[],
): (number | null)[] {
  const last = Math.min(throughDay, days.length === 0 ? throughDay : Math.max(...days));
  let total = 0;
  return values.map((v, i) => {
    if (i + 1 > last) return null;
    total += v;
    return total;
  });
}

function previousPeriod(period: Period): string {
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5, 7));
  return month === 1 ? `${year - 1}-12` : `${year}-${String(month - 1).padStart(2, '0')}`;
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function arrayOf(value: string | string[] | undefined): string[] {
  if (value === undefined) return [];
  return (Array.isArray(value) ? value : [value]).filter((v) => v !== '');
}
